import React, { useState } from 'react';
import type { HSCandidate, HSCodesResponse } from '../services/api';
import { agreementLevel, HS_LEVELS, HsLevel, HsLevelPrediction, rollUpHsPredictions } from '../services/hsLevels';

// One softmax spreads over every code the model can rank, so a declared code far
// from the description scores fractions of a percent; whole percents would show
// it as 0% and hide the difference between a near miss and no fit at all.
export function formatProbability(probability: number): string {
  const percent = probability * 100;
  if (percent >= 10) return `${percent.toFixed(0)}%`;
  if (percent >= 0.1) return `${percent.toFixed(1)}%`;
  return percent > 0 ? '<0.1%' : '0%';
}

function probabilityWidth(probability: number): string {
  return `${Math.max(0, Math.min(1, probability)) * 100}%`;
}

// At hs4 and hs2 there is no single definition, so the group is described by the
// hs6 codes that rolled up into it.
function describe(prediction: HsLevelPrediction, level: HsLevel): string | null {
  if (level === 'hs6') return prediction.definition;
  return prediction.members.map((member) => member.hs6).join(', ');
}

const LEVEL_DIGITS: Record<HsLevel, string> = { hs6: '6', hs4: '4', hs2: '2' };

function scoreText(probability: number, cosine: number): string {
  return `${formatProbability(probability)} · cos ${cosine.toFixed(3)}`;
}

// Where a declared code sits against the suggestions, in the terms the live HS job
// flags on: its own rank when the model ranked it, otherwise the closest level any
// suggestion agrees with it at.
function placement(candidate: HSCandidate, result: HSCodesResponse): string {
  if (!candidate.scored) {
    if (candidate.hs4 === null) return 'Not a six-digit HS code, so it was not scored.';
    if (!result.scored) return 'Nothing in the text could be encoded, so it was not scored.';
    return `hs ${result.modelVersion} has no prototype for this code, so it was not scored.`;
  }

  if (candidate.rank !== null) return `The model's suggestion #${candidate.rank}.`;

  const level = agreementLevel(candidate, result.predictions);
  const outside = `Outside the top ${result.predictions.length}`;
  return level === null
    ? `${outside}; no suggestion shares its chapter.`
    : `${outside}; a suggestion agrees at ${LEVEL_DIGITS[level]} digits.`;
}

interface HsCodeResultProps {
  result: HSCodesResponse;
  onFlag: () => void;
}

const HsCodeResult: React.FC<HsCodeResultProps> = ({ result, onFlag }) => {
  const [level, setLevel] = useState<HsLevel>('hs6');
  const [best, ...alternatives] = rollUpHsPredictions(result.predictions, level);

  return (
    <div className="card result-card">
      <div className="result-header">
        <span className="h5">HS codes</span>
        <span className="tag">hs {result.modelVersion}</span>
      </div>

      {result.predictions.length > 0 && (
        <div className="segmented small" role="radiogroup" aria-label="HS code level">
          {HS_LEVELS.map((option) => (
            <button
              key={option.level}
              type="button"
              role="radio"
              aria-checked={level === option.level}
              className={`segmented-item ${level === option.level ? 'is-active' : ''}`}
              onClick={() => setLevel(option.level)}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}

      {!result.scored && (
        <div className="result-note">
          Nothing in the text could be encoded, so no HS code was ranked.
        </div>
      )}

      {best && (
        <div>
          <div className="prediction-primary">
            <span className="prediction-code">
              {best.code}
              {best.candidate && <span className="tag tag-declared">declared</span>}
            </span>
            <span className="tag tag-score">{scoreText(best.probability, best.cosine)}</span>
          </div>
          {describe(best, level) && <div className="prediction-name">{describe(best, level)}</div>}
          <div className="meter">
            <div className="meter-fill hs" style={{ width: probabilityWidth(best.probability) }} />
          </div>
        </div>
      )}

      {alternatives.length > 0 && (
        <div className="prediction-alternatives">
          {alternatives.map((prediction) => (
            <div key={prediction.code}>
              <div className="prediction-alt-label">
                <span>
                  <strong>{prediction.code}</strong>
                  {prediction.candidate && <span className="tag tag-declared">declared</span>}
                  {describe(prediction, level) && <span> {describe(prediction, level)}</span>}
                </span>
                <span className="score">{scoreText(prediction.probability, prediction.cosine)}</span>
              </div>
              <div className="meter small">
                <div className="meter-fill hs alt" style={{ width: probabilityWidth(prediction.probability) }} />
              </div>
            </div>
          ))}
        </div>
      )}

      {result.candidates.length > 0 && (
        <div className="hs-declared">
          <span className="hs-declared-title">Declared</span>
          {result.candidates.map((candidate) => (
            <div key={candidate.hs6}>
              <div className="prediction-alt-label">
                <span>
                  <strong>{candidate.hs6}</strong>
                  {candidate.definition && <span> {candidate.definition}</span>}
                </span>
                <span className="score">
                  {candidate.scored && candidate.cosine !== null
                    ? scoreText(candidate.probability, candidate.cosine)
                    : 'not scored'}
                </span>
              </div>
              {candidate.scored && (
                <div className="meter small">
                  <div className="meter-fill hs alt" style={{ width: probabilityWidth(candidate.probability) }} />
                </div>
              )}
              <div className="result-note">{placement(candidate, result)}</div>
            </div>
          ))}
        </div>
      )}

      {result.scored && result.predictions.length > 0 && (
        <div className="result-note" style={{ marginTop: '0.75rem' }}>
          {level === 'hs6'
            ? 'Probability is a softmax of cosine over every code the model can rank, so it orders exactly as cosine does and is not calibrated against outcomes. A high score is no guarantee: keyword traps mislead the model.'
            : "The model ranks 6-digit codes only; this level groups them by prefix, summing the group's probability and showing its best cosine."}
        </div>
      )}

      <div className="result-actions">
        <button onClick={onFlag} className="btn btn-outline btn-xs">
          Flag as wrong
        </button>
      </div>
    </div>
  );
};

export default HsCodeResult;
